import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";

/**
 * Uniform date picker used across stage forms.
 *
 * @param value      ISO date string ("YYYY-MM-DD") or ""
 * @param onChange   Called with the ISO string when a day is picked
 * @param testId     data-testid for the trigger button
 * @param disabled   Disables the trigger
 * @param placeholder  Optional string shown when value is empty
 */
export default function DatePicker({ value, onChange, testId, disabled, placeholder = "Pick a date" }) {
  const date = value && isValid(parseISO(value)) ? parseISO(value) : undefined;
  // Controlled Popover so we can close it the moment a day is selected;
  // otherwise the calendar hangs over dialog footers and blocks Save buttons.
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="outline"
          data-testid={testId}
          disabled={disabled}
          className="h-9 w-full justify-start rounded-sm border-neutral-300 text-sm font-normal"
        >
          <CalendarIcon size={14} className="mr-2 text-neutral-500" />
          {date ? format(date, "dd MMM yyyy") : <span className="text-neutral-400">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
          disabled={disabled}
          initialFocus
        />
        {date && (
          <div className="flex justify-end p-2 border-t border-neutral-200">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid={`${testId}-clear`}
              onClick={() => { onChange(""); setOpen(false); }}
              className="text-xs text-neutral-500 hover:text-neutral-800"
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
